import { ReactNode, Dispatch, SetStateAction, useEffect, useMemo, useState, useCallback } from 'react';

import _difference from 'lodash/difference';
import _intersection from 'lodash/intersection';
import _isMatch from 'lodash/isMatch';
import _omit from 'lodash/omit';
import _uniq from 'lodash/uniq';
import _without from 'lodash/without';

import { stub } from '@arrived/common';
import { OfferingSearchQuery, OfferingSearchQueryKeys } from '@arrived/models';

import { FeaturedFilters, FeaturedFilter } from './FeaturedFilters';

import { createUseContext } from '../createUseContext';
import { OfferingFilterValue, QueryFilters } from './types';

const isQueryObject = <T extends string>(obj: OfferingSearchQuery[OfferingSearchQueryKeys]): obj is QueryFilters<T> =>
  typeof obj === 'object' && 'eq' in obj;

export interface OfferingFiltersContextState {
  /**
   * The current selected filter options.
   *
   */
  offeringFilter: OfferingSearchQuery | Record<string, never>;

  /**
   * The number of currently applied filters
   */
  filterCount: number;

  /**
   * Set the query for the updated custom filter you want to apply.
   * @example Adding a new filter
   * ```ts
   * setOfferingFilter('price', 'testing');
   * setOfferingFilter('price', ['testing', 'testing again']);
   * ```
   *
   * @example Removing a filter
   * setOfferingFilter('price', {}); | setOfferingFilter('price');
   *
   * @example Resetting the filter state
   * setOfferingFilter({});
   *
   * TODO: Apply the filter to the query params.
   */
  setOfferingFilter: <T extends OfferingFilterValue>(key?: OfferingSearchQueryKeys, filter?: T) => void;

  /**
   * Check if the filter selected is active
   */
  isFilterActive: <T extends OfferingFilterValue>(key: OfferingSearchQueryKeys, filter?: T) => boolean;

  /**
   * Current index of the selected featured filter
   */
  featuredFilterIdx: number | null;
  setFeaturedFilterIdx: Dispatch<SetStateAction<number | null>>;

  /**
   * Array of featured filters
   */
  featuredFilters: FeaturedFilter[];
}

interface OfferingFiltersContextProviderProps {
  children?: ReactNode;
}

/**
 * Context for managing the filters for the offering page.
 */
export const { Provider: OfferingFiltersContextProvider, useContext: useOfferingFilters } = createUseContext(
  {
    featuredFilterIdx: 0,
    offeringFilter: {},
    filterCount: 0,
    isFilterActive: () => false,
    setFeaturedFilterIdx: stub,
    setOfferingFilter: () => stub,
    featuredFilters: FeaturedFilters,
  } as OfferingFiltersContextState,
  (Provider) =>
    ({ children }: OfferingFiltersContextProviderProps) => {
      // TODO: Parse filter from query params
      const [offeringFilter, _setOfferingFilter] = useState<OfferingSearchQuery>(FeaturedFilters[0].filter);
      const [featuredFilterIdx, setFeaturedFilterIdx] = useState<number | null>(0);

      /**
       * Set the internal state of the filter to the current featured filter
       * if the index changed.
       */
      useEffect(() => {
        if (featuredFilterIdx === null) {
          return;
        }

        _setOfferingFilter(FeaturedFilters[featuredFilterIdx].filter);
      }, [featuredFilterIdx]);

      /**
       * If there is a filter that matches a featured filter then we null that out
       * in favor of custom filters.
       */
      useEffect(() => {
        if (featuredFilterIdx === null) {
          return;
        }

        if (!_isMatch(offeringFilter, FeaturedFilters[featuredFilterIdx].filter)) {
          // Set to null so that we don't overwrite the user's existing filter criteria with a featured filter.
          setFeaturedFilterIdx(null);
        }
      }, [offeringFilter]);

      const isFilterActive = useCallback(
        <T extends OfferingFilterValue>(key: OfferingSearchQueryKeys, filter?: T) => {
          const currentFilter = offeringFilter[key];
          if (isQueryObject(currentFilter)) {
            const currentFilters = currentFilter?.eq;

            if (!currentFilters || currentFilters.length === 0) {
              return filter === null;
            }

            if (Array.isArray(filter)) {
              return _difference(filter, currentFilters).length === 0;
            }

            return currentFilters.some((f) => f === filter);
          }

          return filter === null;
        },
        [offeringFilter]
      );

      const setOfferingFilter = useCallback(
        <T extends OfferingFilterValue>(key?: OfferingSearchQueryKeys | Record<string, never>, filter?: T) => {
          // If we don't pass any key, then we are resetting the filter.
          if (!key || typeof key === 'object') {
            return _setOfferingFilter({});
          }

          // If we pass a key but no filter, the we clear the filter for that key.
          if (filter == null) {
            return _setOfferingFilter((prev) => ({
              ...prev,
              [key]: {
                eq: [],
              },
            }));
          }

          let updatedFilters: OfferingFilterValue[] = [];

          // There is a possibility that the filter is an array, so lets just
          // make everything an array.
          const newFilterArray = Array.isArray(filter) ? filter : [filter];

          // If no filter exists, then we create an empty TermQuery filter
          const currentFilter = offeringFilter[key] ?? { eq: [] };

          if (isQueryObject(currentFilter)) {
            const currentFilters = currentFilter.eq ?? [];

            if (isFilterActive(key, filter)) {
              updatedFilters = _without(currentFilters, ...newFilterArray);
            } else {
              updatedFilters = _uniq([...newFilterArray, ...(currentFilters ?? [])]);
            }

            return _setOfferingFilter((prev) => ({
              ...prev,
              [key]: {
                eq: updatedFilters,
              },
            }));
          }
        },
        [offeringFilter]
      );

      const filterCount = useMemo(
        () =>
          Object.values(offeringFilter).filter((value) => (Array.isArray(value) ? value.length > 0 : value !== null))
            .length,
        [offeringFilter]
      );

      const value = useMemo(
        () => ({
          featuredFilters: FeaturedFilters,
          featuredFilterIdx,
          filterCount,
          setFeaturedFilterIdx,
          offeringFilter,
          setOfferingFilter,
          isFilterActive,
        }),
        [featuredFilterIdx, filterCount, setFeaturedFilterIdx, offeringFilter, setOfferingFilter, isFilterActive]
      );

      return <Provider value={value}>{children}</Provider>;
    }
);
